import React, { useEffect, useRef, useState } from 'react';
import { fromBlob } from 'image-resize-compress';
import { toast } from 'react-toastify';
import './MediaSection.css';

const MAX_GALLERY_IMAGES = 20;
const MAX_THUMBNAIL_IMAGES = 6;
const MAX_VIDEO_SIZE = 30 * 1024 * 1024;
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_THUMBNAIL_SIZE = 500 * 1024;

const MEDIA_CATEGORIES = [
    {
        value: 'cover',
        label: 'Cover'
    },
    {
        value: 'exterior',
        label: 'Exterior'
    },
    {
        value: 'interior',
        label: 'Interior'
    },
    {
        value: 'bedroom',
        label: 'Bedroom'
    },
    {
        value: 'bathroom',
        label: 'Bathroom'
    },
    {
        value: 'kitchen',
        label: 'Kitchen'
    },
    {
        value: 'view',
        label: 'View'
    },
    { value: 'tent', label: 'Tent' },
    { value: 'campsite', label: 'Campsite' },
    { value: 'living_room', label: 'Living Room' },
    { value: 'dining', label: 'Dining' },
    { value: 'balcony', label: 'Balcony' },
    { value: 'pool', label: 'Pool' },
    { value: 'garden', label: 'Garden' },
    { value: 'common_area', label: 'Common Area' },
    { value: 'parking', label: 'Parking' },
    {
        value: 'activity',
        label: 'Activity'
    },
    {
        value: 'other',
        label: 'Other'
    }
];

const createMediaItem = (source, category = 'other') => ({
    url: source,
    category,
    alt: '',
    title: '',
    order: 0
});

const isFile = (value) => typeof File !== 'undefined' && value instanceof File;

const isImage = (value) => {
    if (isFile(value)) {
        return value.type.startsWith('image/');
    }

    if (typeof value === 'string') {
        return true;
    }

    return false;
};

const getPreviewUrl = (item) => {
    if (isFile(item)) {
        return URL.createObjectURL(item);
    }

    if (typeof item === 'string') {
        return item;
    }

    if (item?.url) {
        return item.url;
    }

    if (item?.file && isFile(item.file)) {
        return URL.createObjectURL(item.file);
    }

    return '';
};

const getMediaSource = (item) => {
    if (isFile(item)) {
        return item;
    }

    if (item?.file && isFile(item.file)) {
        return item.file;
    }

    if (typeof item === 'string') {
        return item;
    }

    return item?.url || '';
};

const getMediaCategory = (item) => {
    if (item?.category) {
        return item.category;
    }

    return 'other';
};

const normalizeMedia = (media = []) => {
    if (!Array.isArray(media)) {
        return [];
    }

    return media.map((item, index) => {
        if (typeof item === 'string') {
            return {
                ...createMediaItem(item),
                order: index
            };
        }

        return {
            url: item?.url || '',
            category: item?.category || 'other',
            alt: item?.alt || '',
            title: item?.title || '',
            order: typeof item?.order === 'number' ? item.order : index
        };
    });
};

const MediaSection = ({ formData, updateField, canEdit = true }) => {
    const [galleryFiles, setGalleryFiles] = useState([]);

    const [thumbnailFiles, setThumbnailFiles] = useState([]);

    const [galleryExisting, setGalleryExisting] = useState([]);

    const [thumbnailExisting, setThumbnailExisting] = useState([]);

    const [videoFile, setVideoFile] = useState(null);

    const [videoExisting, setVideoExisting] = useState([]);

    const [galleryLoading, setGalleryLoading] = useState(false);

    const [thumbnailLoading, setThumbnailLoading] = useState(false);

    const [videoLoading, setVideoLoading] = useState(false);

    const [draggedItem, setDraggedItem] = useState(null);

    const [selectedCategory, setSelectedCategory] = useState('other');

    const [editingMedia, setEditingMedia] = useState(null);

    const galleryInputRef = useRef(null);
    const thumbnailInputRef = useRef(null);
    const videoInputRef = useRef(null);

    /*
     * ---------------------------------------------------------
     * LOAD EXISTING MEDIA
     * ---------------------------------------------------------
     */

    /*
     * formData.media/cardImages/videos can contain two very
     * different kinds of entries at this point: items already
     * uploaded (have a `.url`) and items the user picked and
     * compressed on this same visit to this step but hasn't
     * uploaded yet (carry the actual `File` under `.file`, or
     * - for video - are the raw File itself). Uploading only
     * happens once, in handleSubmit, right before the form is
     * submitted (see accommodationMediaUpload.js), so those
     * File-only entries are the normal, expected state whenever
     * the user has added media but not yet submitted.
     *
     * This effect only runs once per mount (empty deps), and
     * MediaSection remounts every time the wizard steps away
     * from Media and back. It used to rebuild gallery/thumbnail
     * state purely from `.url`, filtering out (gallery) or
     * blanking (thumbnails) any entry without one - which threw
     * away not-yet-uploaded File objects entirely, since the
     * local galleryFiles/thumbnailFiles state also resets to []
     * on remount. In practice: pick images, go check an earlier
     * step, come back - the images are gone. Fixed by splitting
     * each source array back into "already uploaded" (existing)
     * vs "picked, pending upload" (Files) instead of discarding
     * the latter.
     */
    useEffect(() => {
        const rawMedia = Array.isArray(formData?.media) ? formData.media : [];

        const existingGallery = [];
        const pendingGalleryFiles = [];

        rawMedia.forEach((item, index) => {
            if (typeof item === 'string') {
                existingGallery.push({ ...createMediaItem(item), order: index });
                return;
            }

            if (item && isFile(item.file)) {
                pendingGalleryFiles.push(item.file);
                return;
            }

            if (item?.url) {
                // keep everything saved on the photo (cover flag, id, ...)
                existingGallery.push({
                    ...item,
                    category: item.category || 'other',
                    alt: item.alt || '',
                    title: item.title || '',
                    order: typeof item.order === 'number' ? item.order : index
                });
            }
        });

        setGalleryExisting(existingGallery);
        setGalleryFiles(pendingGalleryFiles);

        const rawThumbnails = Array.isArray(formData?.cardImages) ? formData.cardImages : [];

        const existingThumbnails = [];
        const pendingThumbnailFiles = [];

        rawThumbnails.forEach((item, index) => {
            if (typeof item === 'string') {
                existingThumbnails.push({ url: item, order: index });
                return;
            }

            if (item && isFile(item.file)) {
                pendingThumbnailFiles.push(item.file);
                return;
            }

            if (item?.url) {
                existingThumbnails.push({
                    url: item.url,
                    order: typeof item.order === 'number' ? item.order : index
                });
            }
        });

        setThumbnailExisting(existingThumbnails);
        setThumbnailFiles(pendingThumbnailFiles);

        const rawVideos = Array.isArray(formData?.videos) ? formData.videos : [];

        const existingVideos = [];
        let pendingVideoFile = null;

        rawVideos.forEach((item) => {
            if (!item) return;

            if (isFile(item)) {
                pendingVideoFile = item;
                return;
            }

            existingVideos.push(item);
        });

        setVideoExisting(existingVideos);
        setVideoFile(pendingVideoFile);
    }, []);

    /*
     * ---------------------------------------------------------
     * IMAGE COMPRESSION
     * ---------------------------------------------------------
     */

    const compressImage = async (file, isThumbnail = false) => {
        const limit = isThumbnail ? MAX_THUMBNAIL_SIZE : MAX_IMAGE_SIZE;

        if (file.size <= limit) {
            return file;
        }

        const quality = isThumbnail ? 20 : 70;

        const compressed = await fromBlob(file, quality, 'auto', 'auto');

        return new File([compressed], file.name, {
            type: compressed.type,
            lastModified: file.lastModified
        });
    };

    /*
     * ---------------------------------------------------------
     * GALLERY UPLOAD
     * ---------------------------------------------------------
     */

    const handleGalleryChange = async (event) => {
        if (!canEdit) return;

        const files = Array.from(event.target.files || []);

        if (!files.length) return;

        const total = galleryExisting.length + galleryFiles.length + files.length;

        if (total > MAX_GALLERY_IMAGES) {
            toast.error(`You can upload only ${MAX_GALLERY_IMAGES} gallery images`);

            event.target.value = '';
            return;
        }

        const invalidFile = files.find((file) => !file.type.startsWith('image/'));

        if (invalidFile) {
            toast.error('Only image files are allowed');

            event.target.value = '';
            return;
        }

        setGalleryLoading(true);

        try {
            const compressedFiles = await Promise.all(files.map((file) => compressImage(file, false)));

            setGalleryFiles((previous) => [...previous, ...compressedFiles]);
        } catch (error) {
            console.error('Gallery compression error:', error);

            toast.error('Image compression failed');
        } finally {
            setGalleryLoading(false);

            event.target.value = '';
        }
    };

    /*
     * ---------------------------------------------------------
     * THUMBNAIL UPLOAD
     * ---------------------------------------------------------
     */

    const handleThumbnailChange = async (event) => {
        if (!canEdit) return;

        const files = Array.from(event.target.files || []);

        if (!files.length) return;

        const total = thumbnailExisting.length + thumbnailFiles.length + files.length;

        if (total > MAX_THUMBNAIL_IMAGES) {
            toast.error(`You can upload only ${MAX_THUMBNAIL_IMAGES} thumbnail images`);

            event.target.value = '';
            return;
        }

        const invalidFile = files.find((file) => !file.type.startsWith('image/'));

        if (invalidFile) {
            toast.error('Only image files are allowed');

            event.target.value = '';
            return;
        }

        setThumbnailLoading(true);

        try {
            const compressedFiles = await Promise.all(files.map((file) => compressImage(file, true)));

            setThumbnailFiles((previous) => [...previous, ...compressedFiles]);
        } catch (error) {
            console.error('Thumbnail compression error:', error);

            toast.error('Thumbnail compression failed');
        } finally {
            setThumbnailLoading(false);

            event.target.value = '';
        }
    };

    /*
     * ---------------------------------------------------------
     * VIDEO
     * ---------------------------------------------------------
     */

    const handleVideoChange = (event) => {
        if (!canEdit) return;

        const file = event.target.files?.[0];

        if (!file) return;

        if (!file.type.startsWith('video/')) {
            toast.error('Please select a valid video file');

            event.target.value = '';
            return;
        }

        if (file.size > MAX_VIDEO_SIZE) {
            toast.error('Video size must be less than 30 MB');

            event.target.value = '';
            return;
        }

        setVideoLoading(true);

        setTimeout(() => {
            setVideoFile(file);
            setVideoExisting([]);
            setVideoLoading(false);
        }, 500);

        event.target.value = '';
    };

    /*
     * ---------------------------------------------------------
     * REMOVE GALLERY
     * ---------------------------------------------------------
     */

    const removeGallery = (index) => {
        if (!canEdit) return;

        const combined = [...galleryExisting, ...galleryFiles];

        const item = combined[index];

        if (!item) return;

        if (index < galleryExisting.length) {
            setGalleryExisting((previous) => previous.filter((_, itemIndex) => itemIndex !== index));

            return;
        }

        const fileIndex = index - galleryExisting.length;

        setGalleryFiles((previous) => previous.filter((_, itemIndex) => itemIndex !== fileIndex));
    };

    /*
     * ---------------------------------------------------------
     * REMOVE THUMBNAIL
     * ---------------------------------------------------------
     */

    const removeThumbnail = (index) => {
        if (!canEdit) return;

        const combined = [...thumbnailExisting, ...thumbnailFiles];

        if (!combined[index]) return;

        if (index < thumbnailExisting.length) {
            setThumbnailExisting((previous) => previous.filter((_, itemIndex) => itemIndex !== index));

            return;
        }

        const fileIndex = index - thumbnailExisting.length;

        setThumbnailFiles((previous) => previous.filter((_, itemIndex) => itemIndex !== fileIndex));
    };

    /*
     * ---------------------------------------------------------
     * VIDEO REMOVE
     * ---------------------------------------------------------
     */

    const removeVideo = () => {
        if (!canEdit) return;

        setVideoFile(null);
        setVideoExisting([]);
    };

    /*
     * ---------------------------------------------------------
     * DRAG START
     * ---------------------------------------------------------
     */

    const handleDragStart = (index, type) => {
        if (!canEdit) return;

        setDraggedItem({
            index,
            type
        });
    };

    /*
     * ---------------------------------------------------------
     * DROP
     * ---------------------------------------------------------
     */

    const handleDrop = (dropIndex, type) => {
        if (!canEdit) return;

        if (!draggedItem) return;

        if (draggedItem.type !== type) {
            setDraggedItem(null);
            return;
        }

        if (draggedItem.index === dropIndex) {
            setDraggedItem(null);
            return;
        }

        if (type === 'gallery') {
            const combined = [...galleryExisting, ...galleryFiles];

            const updated = [...combined];

            const [moved] = updated.splice(draggedItem.index, 1);

            updated.splice(dropIndex, 0, moved);

            const existing = updated.filter((item) => typeof item === 'string' || (!isFile(item) && item?.url));

            const files = updated.filter((item) => isFile(item));

            setGalleryExisting(existing);
            setGalleryFiles(files);
        }

        if (type === 'thumbnail') {
            const combined = [...thumbnailExisting, ...thumbnailFiles];

            const updated = [...combined];

            const [moved] = updated.splice(draggedItem.index, 1);

            updated.splice(dropIndex, 0, moved);

            const existing = updated.filter((item) => typeof item === 'string' || (!isFile(item) && item?.url));

            const files = updated.filter((item) => isFile(item));

            setThumbnailExisting(existing);
            setThumbnailFiles(files);
        }

        setDraggedItem(null);
    };

    /*
     * ---------------------------------------------------------
     * UPDATE FORM MEDIA
     * ---------------------------------------------------------
     */

    useEffect(() => {
        const existing = galleryExisting.map((item, index) => ({
            ...(typeof item === 'string' ? createMediaItem(item) : item),
            order: index
        }));

        const newItems = galleryFiles.map((file, index) => ({
            file,
            category: selectedCategory || 'other',
            alt: '',
            title: '',
            order: existing.length + index
        }));

        updateField('media', [...existing, ...newItems]);
    }, [galleryExisting, galleryFiles]);

    useEffect(() => {
        const existing = thumbnailExisting.map((item, index) => ({
            url: typeof item === 'string' ? item : item?.url || '',
            order: index
        }));

        const newItems = thumbnailFiles.map((file, index) => ({
            file,
            order: existing.length + index
        }));

        updateField('cardImages', [...existing, ...newItems]);
    }, [thumbnailExisting, thumbnailFiles]);

    useEffect(() => {
        const videos = [];

        videoExisting.forEach((video) => videos.push(video));

        if (videoFile) {
            videos.push(videoFile);
        }

        updateField('videos', videos);
    }, [videoExisting, videoFile]);

    /*
     * ---------------------------------------------------------
     * MEDIA CATEGORY
     * ---------------------------------------------------------
     */

    const updateMediaCategory = (index, category) => {
        if (!canEdit) return;

        setGalleryExisting((previous) =>
            previous.map((item, itemIndex) =>
                itemIndex === index
                    ? {
                          ...(typeof item === 'string' ? createMediaItem(item) : item),
                          category
                      }
                    : item
            )
        );
    };

    /*
     * ---------------------------------------------------------
     * COUNTS
     * ---------------------------------------------------------
     */

    const galleryCount = galleryExisting.length + galleryFiles.length;

    const thumbnailCount = thumbnailExisting.length + thumbnailFiles.length;

    const hasVideo = videoExisting.length > 0 || Boolean(videoFile);

    /*
     * ---------------------------------------------------------
     * RENDER
     * ---------------------------------------------------------
     */

    return (
        <div className='media-section'>
            {/* =====================================================
          HEADER
      ====================================================== */}

            <div className='media-header'>
                <div>
                    <span className='section-eyebrow'>PHOTOS & VIDEOS</span>

                    <h2>Media</h2>

                    <p>
                        Upload high-quality property photos and videos. Arrange them in the order guests should see
                        them.
                    </p>
                </div>

                <div className='media-header-stats'>
                    <MediaStat value={`${galleryCount}/${MAX_GALLERY_IMAGES}`} label='Gallery' />

                    <MediaStat value={`${thumbnailCount}/${MAX_THUMBNAIL_IMAGES}`} label='Thumbnails' />

                    <MediaStat value={hasVideo ? '1' : '0'} label='Video' />
                </div>
            </div>

            {/* =====================================================
          GALLERY
      ====================================================== */}

            <MediaUploadCard
                title='Gallery / Wallpaper Images'
                description='These images are used throughout the property detail page.'
                count={galleryCount}
                max={MAX_GALLERY_IMAGES}
                required
                onUpload={() => galleryInputRef.current?.click()}
                canEdit={canEdit}
            >
                <input
                    ref={galleryInputRef}
                    type='file'
                    multiple
                    accept='.jpg,.jpeg,.png,.webp'
                    hidden
                    onChange={handleGalleryChange}
                />

                {galleryLoading ? (
                    <MediaLoading count={Math.max(1, galleryFiles.length)} />
                ) : galleryCount > 0 ? (
                    <>
                        <div className='media-drag-note'>
                            <span>↕</span>

                            <span>Drag and drop images to change their display order.</span>
                        </div>

                        <div className='media-grid'>
                            {[...galleryExisting, ...galleryFiles].map((item, index) => (
                                <MediaImageCard
                                    key={`gallery-${index}`}
                                    item={item}
                                    index={index}
                                    type='gallery'
                                    canEdit={canEdit}
                                    onDragStart={handleDragStart}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={handleDrop}
                                    onRemove={removeGallery}
                                    category={getMediaCategory(item)}
                                    onCategoryChange={updateMediaCategory}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <MediaEmpty
                        icon='🖼️'
                        title='No gallery images'
                        description='Upload at least 5 high-quality images for the property.'
                        onClick={() => galleryInputRef.current?.click()}
                        canEdit={canEdit}
                    />
                )}
            </MediaUploadCard>

            {/* =====================================================
          THUMBNAILS
      ====================================================== */}

            <MediaUploadCard
                title='Thumbnail Images'
                description='Smaller images used in cards, search results and listings.'
                count={thumbnailCount}
                max={MAX_THUMBNAIL_IMAGES}
                required
                onUpload={() => thumbnailInputRef.current?.click()}
                canEdit={canEdit}
            >
                <input
                    ref={thumbnailInputRef}
                    type='file'
                    multiple
                    accept='.jpg,.jpeg,.png,.webp'
                    hidden
                    onChange={handleThumbnailChange}
                />

                {thumbnailLoading ? (
                    <MediaLoading count={2} />
                ) : thumbnailCount > 0 ? (
                    <>
                        <div className='media-drag-note'>
                            <span>↕</span>

                            <span>Drag and drop to arrange the thumbnail order.</span>
                        </div>

                        <div className='thumbnail-grid'>
                            {[...thumbnailExisting, ...thumbnailFiles].map((item, index) => (
                                <MediaImageCard
                                    key={`thumbnail-${index}`}
                                    item={item}
                                    index={index}
                                    type='thumbnail'
                                    canEdit={canEdit}
                                    onDragStart={handleDragStart}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={handleDrop}
                                    onRemove={removeThumbnail}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <MediaEmpty
                        icon='📸'
                        title='No thumbnail images'
                        description='Upload at least 2 images for property cards.'
                        onClick={() => thumbnailInputRef.current?.click()}
                        canEdit={canEdit}
                    />
                )}
            </MediaUploadCard>

            {/* =====================================================
          VIDEO
      ====================================================== */}

            <MediaUploadCard
                title='Property Video'
                description='Add a short walkthrough or promotional video.'
                count={hasVideo ? 1 : 0}
                max={1}
                onUpload={() => videoInputRef.current?.click()}
                canEdit={canEdit}
            >
                <input
                    ref={videoInputRef}
                    type='file'
                    accept='video/mp4,video/webm,video/ogg'
                    hidden
                    onChange={handleVideoChange}
                />

                {videoLoading ? (
                    <MediaLoading count={1} />
                ) : hasVideo ? (
                    <VideoPreview video={videoFile || videoExisting[0]} onRemove={removeVideo} canEdit={canEdit} />
                ) : (
                    <MediaEmpty
                        icon='🎥'
                        title='No property video'
                        description='MP4, WebM or OGG video up to 30 MB.'
                        onClick={() => videoInputRef.current?.click()}
                        canEdit={canEdit}
                    />
                )}
            </MediaUploadCard>

            {/* =====================================================
          MEDIA GUIDELINES
      ====================================================== */}

            <div className='media-guidelines'>
                <div className='media-guidelines-icon'>💡</div>

                <div>
                    <strong>Media guidelines</strong>

                    <ul>
                        <li>Gallery supports up to {MAX_GALLERY_IMAGES} images.</li>

                        <li>Thumbnail section supports up to {MAX_THUMBNAIL_IMAGES} images.</li>

                        <li>Use JPG, PNG or WebP for images.</li>

                        <li>Video size must not exceed 30 MB.</li>

                        <li>Upload clear images showing the actual property and facilities.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   MEDIA STAT
========================================================= */

const MediaStat = ({ value, label }) => {
    return (
        <div className='media-stat'>
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    );
};

/* =========================================================
   UPLOAD CARD
========================================================= */

const MediaUploadCard = ({ title, description, count, max, required = false, onUpload, canEdit, children }) => {
    return (
        <div className='media-upload-card'>
            <div className='media-upload-header'>
                <div>
                    <div className='media-title-row'>
                        <h3>{title}</h3>

                        {required && <span className='media-required'>Required</span>}
                    </div>

                    <p>{description}</p>
                </div>

                <div className='media-upload-actions'>
                    <span className='media-count'>
                        {count}/{max}
                    </span>

                    {canEdit && count < max && (
                        <button type='button' className='media-add-btn' onClick={onUpload}>
                            + Add
                        </button>
                    )}
                </div>
            </div>

            <div className='media-upload-body'>{children}</div>
        </div>
    );
};

/* =========================================================
   IMAGE CARD
========================================================= */

const MediaImageCard = ({
    item,
    index,
    type,
    canEdit,
    onDragStart,
    onDragOver,
    onDrop,
    onRemove,
    category,
    onCategoryChange
}) => {
    const [preview, setPreview] = useState('');

    useEffect(() => {
        const url = getPreviewUrl(item);

        setPreview(url);

        return () => {
            if (isFile(item) && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        };
    }, [item]);

    return (
        <div
            className={`media-image-card ${type === 'thumbnail' ? 'thumbnail-card' : ''}`}
            draggable={canEdit}
            onDragStart={() => onDragStart(index, type)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(index, type)}
        >
            <div className='media-image-wrapper'>
                {preview ? (
                    <img src={preview} alt={item?.alt || `Property image ${index + 1}`} />
                ) : (
                    <div className='media-image-placeholder'>🖼️</div>
                )}

                <div className='media-image-overlay'>
                    <span className='media-drag-handle'>⠿</span>

                    <span className='media-order'>{index + 1}</span>
                </div>

                {canEdit && (
                    <button
                        type='button'
                        className='media-remove-btn'
                        onClick={() => onRemove(index)}
                        aria-label='Remove image'
                    >
                        ×
                    </button>
                )}
            </div>

            {type === 'gallery' && onCategoryChange && (
                <div className='media-category'>
                    <select
                        value={category || 'other'}
                        onChange={(event) => onCategoryChange(index, event.target.value)}
                        disabled={!canEdit}
                    >
                        {MEDIA_CATEGORIES.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className='media-image-footer'>
                <span>{isFile(item) ? 'New image' : 'Existing image'}</span>
            </div>
        </div>
    );
};

/* =========================================================
   VIDEO PREVIEW
========================================================= */

const VideoPreview = ({ video, onRemove, canEdit }) => {
    const [preview, setPreview] = useState('');

    useEffect(() => {
        if (isFile(video)) {
            const url = URL.createObjectURL(video);

            setPreview(url);

            return () => URL.revokeObjectURL(url);
        }

        if (typeof video === 'string') {
            setPreview(video);
        }
    }, [video]);

    return (
        <div className='media-video-wrapper'>
            <div className='media-video-preview'>
                {preview ? (
                    <video src={preview} controls playsInline />
                ) : (
                    <div className='media-video-placeholder'>🎥</div>
                )}

                {canEdit && (
                    <button type='button' className='media-video-remove' onClick={onRemove}>
                        ×
                    </button>
                )}
            </div>

            <div className='media-video-info'>
                <div>
                    <strong>{isFile(video) ? video.name : 'Existing property video'}</strong>

                    <span>{isFile(video) ? `${(video.size / (1024 * 1024)).toFixed(2)} MB` : 'Uploaded video'}</span>
                </div>

                <span className='video-status'>Ready</span>
            </div>
        </div>
    );
};

/* =========================================================
   EMPTY STATE
========================================================= */

const MediaEmpty = ({ icon, title, description, onClick, canEdit }) => {
    return (
        <div className='media-empty'>
            <div className='media-empty-icon'>{icon}</div>

            <strong>{title}</strong>

            <p>{description}</p>

            {canEdit && (
                <button type='button' onClick={onClick}>
                    Choose Files
                </button>
            )}
        </div>
    );
};

/* =========================================================
   LOADING
========================================================= */

const MediaLoading = ({ count = 2 }) => {
    return (
        <div className='media-loading-grid'>
            {Array.from({
                length: count
            }).map((_, index) => (
                <div className='media-loading-card' key={index}>
                    <div className='media-loading-image' />

                    <div className='media-loading-line' />

                    <div className='media-loading-small' />
                </div>
            ))}
        </div>
    );
};

export default MediaSection;
